import { Alert, Button, Group, Modal, SimpleGrid, Stack, TextInput } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, podatki, type Shema } from '../api/odjemalec';
import { PomocIkona } from '../pomoc/PomocIkona';
import { razdeliIme, vrsta as poKljucu, type Vrsta } from './vrste';

type Oseba = Shema['OsebaPodatki'];

const POLJA = [
  ['ime', 'Ime'],
  ['priimek', 'Priimek'],
  ['davcna', 'Davčna'],
  ['idZaDdv', 'ID za DDV'],
  ['ulica', 'Ulica'],
  ['hisnaStevilka', 'Hišna št.'],
  ['postnaStevilka', 'Poštna št.'],
  ['kraj', 'Kraj'],
  ['drzava', 'Država'],
  ['tel1', 'Telefon'],
  ['email', 'E-pošta'],
] as const;
type Polje = (typeof POLJA)[number][0];
type Vnos = Record<Polje, string>;

const prazen = Object.fromEntries(POLJA.map(([k]) => [k, ''])) as Vnos;
const vrednost = (s: string) => s.trim() || undefined;

/**
 * Nova oseba iz izbire osebe (kot gumb »Nov kupec« v iskalnem oknu Swinga): okno z osnovnimi, naslovnimi in
 * kontaktnimi podatki, ime in priimek predizpolnjena iz iskanja ({@link razdeliIme}). Shrani se z enim klicem
 * ({@code POST /api/osebe/{vrsta}/nova}), nova oseba se takoj izbere; ostalo se dopolni na zaslonu osebe.
 */
export function NovaOseba({
  vrsta,
  odprto,
  iskanje,
  zapri,
  dodana,
}: {
  vrsta: Vrsta['kljuc'];
  odprto: boolean;
  iskanje: string;
  zapri: () => void;
  dodana: (o: Oseba) => void;
}) {
  return (
    <Modal
      opened={odprto}
      onClose={zapri}
      title={
        <Group gap={4} wrap="nowrap">
          {poKljucu(vrsta).nova}
          <PomocIkona clanek="nov-kupec" />
        </Group>
      }
      size="lg"
    >
      {odprto && <Obrazec vrsta={vrsta} iskanje={iskanje} zapri={zapri} dodana={dodana} />}
    </Modal>
  );
}

/** Vsebina okna; ob vsakem odprtju nova, z imenom in priimkom iz iskanja. */
function Obrazec({
  vrsta,
  iskanje,
  zapri,
  dodana,
}: {
  vrsta: Vrsta['kljuc'];
  iskanje: string;
  zapri: () => void;
  dodana: (o: Oseba) => void;
}) {
  const odjemalec = useQueryClient();
  const [vnos, setVnos] = useState<Vnos>(() => ({ ...prazen, ...razdeliIme(iskanje) }));
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [shranjujem, setShranjujem] = useState(false);

  const shrani = async () => {
    setShranjujem(true);
    setPolja({});
    setNapaka(null);
    try {
      const o = await podatki(
        api.POST('/api/osebe/{vrsta}/nova', {
          params: { path: { vrsta } },
          body: {
            oseba: {
              id: 0,
              ime: vnos.ime.trim(),
              priimek: vrednost(vnos.priimek),
              davcna: vrednost(vnos.davcna),
              idZaDdv: vrednost(vnos.idZaDdv),
              ulica: vrednost(vnos.ulica),
              hisnaStevilka: vrednost(vnos.hisnaStevilka),
              postnaStevilka: vrednost(vnos.postnaStevilka),
              kraj: vrednost(vnos.kraj),
              drzava: vrednost(vnos.drzava),
            },
            podrobnosti: { tel1: vrednost(vnos.tel1), email: vrednost(vnos.email) },
          },
        }),
      );
      await odjemalec.invalidateQueries({ queryKey: ['osebe'] });
      await odjemalec.invalidateQueries({ queryKey: ['izbira-osebe', vrsta] });
      dodana(o);
      zapri();
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setShranjujem(false);
    }
  };

  const vnosPolja = ([k, oznaka]: (typeof POLJA)[number]) => (
    <TextInput
      key={k}
      label={oznaka}
      value={vnos[k]}
      onChange={(e) => {
        const x = e.currentTarget.value;
        setVnos((s) => ({ ...s, [k]: x }));
      }}
      error={polja[k]}
      withAsterisk={k === 'ime'}
      data-autofocus={k === 'ime' ? true : undefined}
      type={k === 'email' ? 'email' : undefined}
      inputMode={k === 'tel1' ? 'tel' : undefined}
    />
  );

  return (
    <form
      onSubmit={(e) => {
        // React dogodek skozi portal doseže tudi obrazec, v katerem je izbira osebe
        e.preventDefault();
        e.stopPropagation();
        void shrani();
      }}
    >
      <Stack gap="sm">
        {napaka && (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} role="alert">
            {napaka}
          </Alert>
        )}
        <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
          {POLJA.map(vnosPolja)}
        </SimpleGrid>
        <Group justify="flex-end" gap="xs" mt="xs">
          <Button variant="subtle" onClick={zapri} disabled={shranjujem}>
            Prekliči
          </Button>
          <Button type="submit" loading={shranjujem}>
            Dodaj
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
